import * as React from 'react';
import {IHubSearchData, ISearchData} from 'components/hooks/hub/useSearchHubData';

const DEFAULT_LIMIT = 50;

interface IUseHubSearchLazyLoadConfigContext<T, U> {
  items: T[];
  totalCount: number;
  isLazyLoading: boolean;
  lazyLoad: () => void;
  isError: boolean;
  isLoading: boolean;
  search: (params: U) => void;
  reset: (resetForm?: boolean) => void;
  searchParams: U;
  error: any;
}

// T type returned by the endpoint
// U params for the endpoint
// V useProvider hook
export const getHubSearchLazyLoadProviderContext = <
  T,
  U extends Record<string, unknown>,
  V extends IHubSearchData<ISearchData<T>, U>,
>(): {
  HubSearchContext: React.Context<IUseHubSearchLazyLoadConfigContext<T, U> | null>;
  HubSearchLazyLoadProvider: React.FC<{useProvider: () => V}>;
  useHubSearchLazyLoadProvider: () => IUseHubSearchLazyLoadConfigContext<T, U>;
} => {
  const HubSearchContext = React.createContext<IUseHubSearchLazyLoadConfigContext<T, U> | null>(null);

  return {
    HubSearchContext,
    HubSearchLazyLoadProvider: ({children, useProvider}) => {
      const offsetRef = React.useRef<number>(0);
      const paramsRef = React.useRef<U>({} as U);
      const hasMoreRef = React.useRef<boolean>(false);

      const [itemsData, setItemsData] = React.useState<{items: T[]; totalCount: number}>({
        items: [],
        totalCount: 0,
      });

      const {items, totalCount} = itemsData;

      const {isFetching, isError, data: data, search: doSearch, error} = useProvider();

      const search = React.useCallback(
        (params?: U) => {
          hasMoreRef.current = false;
          paramsRef.current = params ?? ({} as U);
          offsetRef.current = 0;

          setItemsData(prev => ({items: [], totalCount: prev.totalCount}));
          doSearch({...(params as U), offset: offsetRef.current, limit: DEFAULT_LIMIT});
        },
        [doSearch],
      );

      const reset = React.useCallback((resetForm?: boolean) => {
        setItemsData({items: [], totalCount: 0});
        offsetRef.current = 0;
        paramsRef.current = resetForm ? ({} as U) : paramsRef.current;
        hasMoreRef.current = false;
      }, []);

      React.useEffect(() => {
        if (isFetching || !data) return;

        if (!data?.data?.length) {
          if (offsetRef.current === 0) {
            setItemsData({items: [], totalCount: 0});
          }
          hasMoreRef.current = false;
        } else {
          setItemsData(prevData => ({
            items: offsetRef.current === 0 ? data?.data : [...prevData.items, ...data?.data],
            totalCount: data?.totalCount,
          }));

          hasMoreRef.current = data?.data?.length < DEFAULT_LIMIT ? false : true;
        }
      }, [data, isFetching]);

      const lazyLoad = React.useCallback(() => {
        if (!hasMoreRef.current || !items.length) return;
        offsetRef.current = offsetRef.current + DEFAULT_LIMIT;
        doSearch({...paramsRef.current, offset: offsetRef.current, limit: DEFAULT_LIMIT});
      }, [doSearch, items.length]);

      const value = React.useMemo(
        () => ({
          items,
          totalCount,
          isLazyLoading: isFetching && offsetRef.current > 0,
          lazyLoad,
          reset,
          isLoading: isFetching && offsetRef.current === 0,
          isError,
          search,
          searchParams: paramsRef.current,
          error,
        }),
        [isError, isFetching, items, lazyLoad, reset, search, totalCount, error],
      );

      return <HubSearchContext.Provider value={value}>{children}</HubSearchContext.Provider>;
    },
    useHubSearchLazyLoadProvider: () => {
      const context = React.useContext(HubSearchContext);
      if (!context) {
        throw new Error('useHubSearchLazyLoadProvider must be used within a HubSearchLazyLoadProvider');
      }
      return context;
    },
  };
};
